import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Paperclip, Send, Minus, MessageCircle, Inbox, CornerDownRight, RefreshCw } from 'lucide-react';
import { API_BASE } from '../../utils/apiConfig';

const fileUrl = (u) => (u && u.startsWith('/') ? `${API_BASE}${u}` : u);

const ContactWidget = ({ isOpen, onClose, primaryColor }) => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('form'); // 'form' | 'replies' | 'whatsapp'
  const [myMessages, setMyMessages] = useState([]);
  const [lookupEmail, setLookupEmail] = useState('');
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  // Prefill email from a logged-in user if available.
  useEffect(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const u = JSON.parse(stored);
        const email = u?.email_id || u?.email;
        if (email) {
          setLookupEmail(email);
          setFormData((p) => ({ ...p, email: p.email || email, name: p.name || u?.name || '' }));
        }
      }
    } catch { /* ignore */ }
  }, []);

  const fetchMyMessages = async (email) => {
    const e = (email || lookupEmail || '').trim();
    if (!e) return;
    setLoadingMsgs(true);
    try {
      const res = await fetch(`/api/support/by-email/${encodeURIComponent(e)}`);
      const data = res.ok ? await res.json() : [];
      setMyMessages(Array.isArray(data) ? data : []);
    } catch {
      setMyMessages([]);
    } finally {
      setLoadingMsgs(false);
    }
  };

  // Per-ticket reply composer state.
  const [replyText, setReplyText] = useState({});
  const [sendingReply, setSendingReply] = useState(null);

  const sendUserReply = async (ticketId) => {
    const text = (replyText[ticketId] || '').trim();
    if (!text) return;
    setSendingReply(ticketId);
    try {
      const fd = new FormData();
      fd.append('text', text);
      fd.append('by', 'user');
      const res = await fetch(`/api/support/${ticketId}/reply`, { method: 'POST', body: fd });
      if (res.ok) {
        setReplyText((p) => ({ ...p, [ticketId]: '' }));
        await fetchMyMessages(lookupEmail);
      }
    } catch {
      /* ignore */
    } finally {
      setSendingReply(null);
    }
  };

  // Auto-load replies when switching to the replies tab with a known email.
  useEffect(() => {
    if (activeTab === 'replies' && lookupEmail) fetchMyMessages(lookupEmail);
  }, [activeTab]); // eslint-disable-line

  useEffect(() => {
    fetch('/api/settings/support-widget')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data) {
          setWidgetConfig(data);
          // Default to whatsapp tab if form is disabled
          if (!data.messageFormEnabled && data.whatsappEnabled) {
            setActiveTab('whatsapp');
          }
        }
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!formData.email || !formData.message) return;
    try {
      setLoading(true);
      const data = new FormData();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('message', formData.message);
      files.forEach((file) => data.append('files', file));
      const res = await fetch('/api/support', { method: 'POST', body: data });
      if (res.ok) {
        onClose();
        setFormData({ name: '', email: '', message: '' });
        setFiles([]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length + files.length > 5) return;
    setFiles([...files, ...selected]);
  };

  const openWhatsApp = () => {
    if (!widgetConfig?.whatsappNumber) return;
    const phone = widgetConfig.whatsappNumber.replace(/\D/g, '');
    const msg = encodeURIComponent(widgetConfig.whatsappMessage || '');
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };

  if (!isOpen) return null;

  const showWhatsApp = widgetConfig?.whatsappEnabled && widgetConfig?.whatsappNumber;
  const showForm = widgetConfig === null || widgetConfig?.messageFormEnabled;
  const showTabs = showWhatsApp && showForm;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 50, scale: 0.9 }}
      className="fixed bottom-24 right-6 w-[min(calc(100vw-32px),320px)] bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-slate-100 overflow-hidden z-[110] flex flex-col font-outfit"
    >
      {/* Header */}
      <div className="bg-[#0f172a] p-4 flex items-center justify-between text-white">
        <h3 className="text-base font-bold tracking-tight">Leave us a message</h3>
        <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-lg transition-colors">
          <Minus size={20} />
        </button>
      </div>

      {/* Tabs */}
      {showForm && (
        <div className="flex border-b border-slate-100">
          <button
            onClick={() => setActiveTab('form')}
            className={`flex-1 py-2.5 text-[11px] font-black uppercase tracking-wide transition-colors ${activeTab === 'form' ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-400'}`}
          >
            Message
          </button>
          <button
            onClick={() => setActiveTab('replies')}
            className={`flex-1 py-2.5 text-[11px] font-black uppercase tracking-wide transition-colors flex items-center justify-center gap-1.5 ${activeTab === 'replies' ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-400'}`}
          >
            <Inbox size={12} /> Replies
          </button>
          {showWhatsApp && (
            <button
              onClick={() => setActiveTab('whatsapp')}
              className={`flex-1 py-2.5 text-[11px] font-black uppercase tracking-wide transition-colors flex items-center justify-center gap-1.5 ${activeTab === 'whatsapp' ? 'text-green-600 border-b-2 border-green-500' : 'text-slate-400'}`}
            >
              <MessageCircle size={12} /> WhatsApp
            </button>
          )}
        </div>
      )}

      {/* WhatsApp Panel */}
      {showWhatsApp && (!showForm || activeTab === 'whatsapp') && (
        <div className="p-6 flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
            <MessageCircle size={32} className="text-green-500" />
          </div>
          <div>
            <p className="text-sm font-black text-slate-900">Chat on WhatsApp</p>
            <p className="text-[11px] text-slate-500 mt-1">We usually reply within minutes</p>
          </div>
          <button
            onClick={openWhatsApp}
            className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl font-black text-sm transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-green-500/20 flex items-center justify-center gap-2"
          >
            <MessageCircle size={16} />
            Open WhatsApp
          </button>
        </div>
      )}

      {/* Message Form */}
      {showForm && activeTab === 'form' && (
        <>
          <div className="p-4 space-y-3 max-h-[40vh] overflow-y-auto no-scrollbar">
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">Your name (optional)</label>
              <input
                type="text"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-slate-900 outline-none transition-all text-xs font-medium"
                placeholder=""
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">Email address</label>
              <input
                type="email"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-slate-900 outline-none transition-all text-xs font-medium"
                placeholder=""
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">How can we help you?</label>
              <textarea
                rows={2}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-slate-900 outline-none transition-all text-xs font-medium resize-none"
                placeholder=""
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">
                Attachments {files.length > 0 && <span className="text-blue-500">({files.length}/5)</span>}
              </label>
              <div
                onClick={() => document.getElementById('support-file-input').click()}
                className="w-full border-2 border-dashed border-slate-200 rounded-2xl p-3 flex flex-col items-center justify-center gap-1 hover:bg-slate-50 transition-all cursor-pointer group"
              >
                <input
                  id="support-file-input"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                  accept="image/*,.pdf,.doc,.docx"
                />
                <div className="p-1.5 bg-slate-50 rounded-full group-hover:bg-white transition-colors">
                  <Paperclip size={16} className="text-slate-400 group-hover:text-slate-900" />
                </div>
                <span className="text-[10px] font-bold text-slate-500 group-hover:text-slate-900 transition-colors uppercase tracking-tight">
                  {files.length > 0 ? `${files.length} files selected` : 'Add up to 5 files'}
                </span>
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-300">SUPPORT</span>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-[#0f172a] text-white px-6 py-2 rounded-xl font-bold text-xs hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-slate-900/20 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send'}
            </button>
          </div>
        </>
      )}

      {/* My Replies Panel */}
      {showForm && activeTab === 'replies' && (
        <div className="p-4 space-y-3 max-h-[50vh] overflow-y-auto no-scrollbar">
          <div className="flex items-center gap-2">
            <input
              type="email"
              className="flex-1 px-3 py-2 rounded-xl border border-slate-200 focus:border-slate-900 outline-none transition-all text-xs font-medium"
              placeholder=""
              value={lookupEmail}
              onChange={(e) => setLookupEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchMyMessages()}
            />
            <button
              onClick={() => fetchMyMessages()}
              className="p-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors shrink-0"
              title="Load my messages"
            >
              <RefreshCw size={14} className={loadingMsgs ? 'animate-spin' : ''} />
            </button>
          </div>

          {loadingMsgs ? (
            <p className="text-center text-[11px] font-bold text-slate-400 py-6">Loading your messages...</p>
          ) : myMessages.length === 0 ? (
            <div className="text-center py-8">
              <Inbox size={28} className="mx-auto text-slate-200 mb-2" />
              <p className="text-[11px] font-bold text-slate-400">No messages found for this email.</p>
            </div>
          ) : (
            myMessages.map((m) => {
              const replies = Array.isArray(m.metadata?.replies) ? m.metadata.replies : [];
              return (
                <div key={m.id} className="border border-slate-100 rounded-2xl overflow-hidden">
                  <div className="p-3 bg-slate-50/70">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Ticket #{m.id}</span>
                      <span className="text-[9px] font-black uppercase tracking-widest text-blue-500">{m.status}</span>
                    </div>
                    <p className="text-xs font-medium text-slate-700 break-words">{m.message}</p>
                  </div>
                  {replies.length > 0 ? (
                    <div className="p-3 space-y-2">
                      {replies.map((rep, idx) => (
                        <div key={idx} className="flex gap-2 bg-orange-50 border border-orange-100 rounded-xl p-2.5">
                          <CornerDownRight size={13} className="text-orange-400 shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            {rep.text && <p className="text-[11px] font-medium text-slate-700 break-words">{rep.text}</p>}
                            {rep.attachments?.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {rep.attachments.map((url, ai) => (
                                  <a key={ai} href={fileUrl(url)} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-2 py-1 bg-white border border-orange-200 rounded-lg text-[9px] font-black text-orange-600 uppercase">
                                    <Paperclip size={10} /> File {ai + 1}
                                  </a>
                                ))}
                              </div>
                            )}
                            <p className="text-[8px] font-bold text-slate-400 uppercase mt-1">{rep.by === 'user' ? 'You' : 'Support'} · {rep.at ? new Date(rep.at).toLocaleString() : ''}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="px-3 py-2.5 text-[10px] font-bold text-slate-400 italic">Awaiting reply from support…</p>
                  )}

                  {/* User reply composer — hidden once the ticket is closed */}
                  {m.status !== 'Closed' ? (
                    <div className="flex items-center gap-2 p-2.5 border-t border-slate-100">
                      <input
                        type="text"
                        value={replyText[m.id] || ''}
                        onChange={(e) => setReplyText((p) => ({ ...p, [m.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendUserReply(m.id); } }}
                        placeholder="Write a reply…"
                        className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-600/20"
                      />
                      <button
                        type="button"
                        onClick={() => sendUserReply(m.id)}
                        disabled={sendingReply === m.id || !(replyText[m.id] || '').trim()}
                        className="shrink-0 h-9 w-9 flex items-center justify-center rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-all disabled:opacity-40"
                      >
                        <Send size={15} />
                      </button>
                    </div>
                  ) : (
                    <p className="px-3 py-2 text-[9px] font-bold text-slate-400 uppercase border-t border-slate-100">This ticket is closed.</p>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </motion.div>
  );
};

export default ContactWidget;
